import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'cta'
type Size = 'sm' | 'md' | 'lg'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
}

// Стиль Attio Premium v2 — синхронизирован с .btn-* классами в globals.css.
// primary   — почти-чёрный (ink-900), главное действие на форме
// cta       — брендовый синий (accent-500), главное действие на странице
// secondary — белый с тонкой 0.5px границей
// ghost     — без рамки, только hover-фон
// danger    — красный outline (для подтверждений удаления)
const variants: Record<Variant, string> = {
  primary:   'bg-ink-900 text-white hover:bg-ink-800 active:bg-ink-700',
  cta:       'bg-accent-500 text-white hover:bg-accent-600 active:bg-accent-700',
  secondary: 'border-hairline border-ink-200 bg-white text-ink-700 hover:bg-ink-50 hover:border-ink-250 hover:text-ink-900',
  ghost:     'text-ink-700 hover:bg-ink-100 hover:text-ink-900',
  danger:    'border-hairline border-red-200 bg-white text-red-700 hover:bg-red-50',
}

const sizes: Record<Size, string> = {
  sm: 'px-3 py-1.5 text-[13px] gap-1.5 rounded-[10px]',
  md: 'px-4 py-2 text-sm gap-2 rounded-[10px]',
  lg: 'px-5 py-2.5 text-[15px] gap-2 rounded-[10px]',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', className, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center font-semibold transition-colors duration-200',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        variants[variant],
        sizes[size],
        className
      )}
      {...props}
    />
  )
)
Button.displayName = 'Button'
